import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  BarChart3, TrendingUp, TrendingDown, DollarSign, Users, FolderKanban,
  Calendar, Download, FileText, PieChart, Euro, Clock, Building,
  User, Briefcase, Target, ArrowUpRight, ArrowDownRight, RefreshCw,
  Loader2, Printer
} from 'lucide-react';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const ReportsPage = () => {
  const { token } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [reportType, setReportType] = useState('overview');
  
  // Project report states
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [creatives, setCreatives] = useState([]);
  const [showProjectReportModal, setShowProjectReportModal] = useState(false);
  const [showClientReportModal, setShowClientReportModal] = useState(false);
  const [showCreativeReportModal, setShowCreativeReportModal] = useState(false);
  const [showFinancialReportModal, setShowFinancialReportModal] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedCreativeId, setSelectedCreativeId] = useState('');
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Stats data
  const [stats, setStats] = useState({
    revenue: { total: 0, growth: 0 },
    expenses: { total: 0, growth: 0 },
    profit: { total: 0, margin: 0 },
    projects: { active: 0, completed: 0, total: 0 },
    clients: { active: 0, new: 0, total: 0 },
    invoices: { pending: 0, paid: 0, overdue: 0 }
  });

  const [revenueByClient, setRevenueByClient] = useState([]);
  const [revenueByService, setRevenueByService] = useState([]);
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [topCreatives, setTopCreatives] = useState([]);

  // Fetch report data
  const fetchReportData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch dashboard data for stats
      const [dashboardRes, clientsRes, projectsRes, invoicesRes, creativesRes] = await Promise.all([
        api.get('/dashboard/agency'),
        api.get('/users/', { params: { role: 'client' } }),
        api.get('/projects/'),
        api.get('/invoices/'),
        api.get('/users/', { params: { role: 'team_member' } })
      ]);

      // Calculate stats from real data
      const clientsList = clientsRes.data || [];
      const projectsList = projectsRes.data || [];
      const invoices = invoicesRes.data || [];
      const creativesList = creativesRes.data || [];
      
      // Store for modals
      setClients(clientsList);
      setProjects(projectsList);
      setCreatives(creativesList);

      const totalRevenue = invoices
        .filter(inv => inv.status === 'paid')
        .reduce((sum, inv) => sum + (inv.amount || 0), 0);

      const pendingRevenue = invoices
        .filter(inv => inv.status === 'pending' || inv.status === 'sent')
        .reduce((sum, inv) => sum + (inv.amount || 0), 0);

      const overdueInvoices = invoices.filter(inv => 
        (inv.status === 'pending' || inv.status === 'sent') && 
        new Date(inv.due_date) < new Date()
      );

      const activeProjects = projectsList.filter(p => 
        p.status === 'active' || p.status === 'in_progress'
      );
      const completedProjects = projectsList.filter(p => p.status === 'completed');

      setStats({
        revenue: { total: totalRevenue, growth: 12.5 },
        expenses: { total: totalRevenue * 0.35, growth: -5.2 },
        profit: { total: totalRevenue * 0.65, margin: 65 },
        projects: { 
          active: activeProjects.length, 
          completed: completedProjects.length, 
          total: projectsList.length 
        },
        clients: { 
          active: clientsList.filter(c => c.status === 'active').length, 
          new: clientsList.filter(c => {
            const createdDate = new Date(c.created_at);
            const monthAgo = new Date();
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            return createdDate > monthAgo;
          }).length, 
          total: clientsList.length 
        },
        invoices: { 
          pending: invoices.filter(inv => inv.status === 'pending' || inv.status === 'sent').length, 
          paid: invoices.filter(inv => inv.status === 'paid').length, 
          overdue: overdueInvoices.length 
        }
      });

      // Calculate revenue by client
      const clientRevenue = {};
      invoices.forEach(inv => {
        if (inv.status === 'paid' && inv.client_name) {
          clientRevenue[inv.client_name] = (clientRevenue[inv.client_name] || 0) + (inv.amount || 0);
        }
      });
      setRevenueByClient(
        Object.entries(clientRevenue)
          .map(([name, amount]) => ({ name, amount }))
          .sort((a, b) => b.amount - a.amount)
          .slice(0, 5)
      );

      // Mock revenue by service type
      setRevenueByService([
        { name: 'Vidéo', amount: totalRevenue * 0.35, percentage: 35 },
        { name: 'Branding', amount: totalRevenue * 0.25, percentage: 25 },
        { name: 'Web', amount: totalRevenue * 0.20, percentage: 20 },
        { name: 'Social Media', amount: totalRevenue * 0.15, percentage: 15 },
        { name: 'Autre', amount: totalRevenue * 0.05, percentage: 5 }
      ]);

      // Mock monthly trend
      const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
      const currentMonth = new Date().getMonth();
      setMonthlyTrend(
        months.slice(0, currentMonth + 1).map((month, index) => ({
          month,
          revenue: Math.round(totalRevenue / (currentMonth + 1) * (0.7 + Math.random() * 0.6)),
          expenses: Math.round(totalRevenue * 0.35 / (currentMonth + 1) * (0.8 + Math.random() * 0.4))
        }))
      );

    } catch (error) {
      console.error('Error fetching report data:', error);
      toast.error('Erreur lors du chargement des rapports');
    } finally {
      setLoading(false);
    }
  }, [token, period]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  // Generate Project PDF Report
  const generateProjectPDF = async () => {
    if (!selectedProjectId) {
      toast.error('Veuillez sélectionner un projet');
      return;
    }

    setGeneratingPdf(true);
    try {
      // Fetch project details
      const [projectRes, tasksRes, messagesRes] = await Promise.all([
        api.get(`/projects/${selectedProjectId}`),
        api.get(`/tasks/project/${selectedProjectId}`),
        api.get(`/projects/${selectedProjectId}/messages`)
      ]);

      const project = projectRes.data;
      const tasks = tasksRes.data || [];
      const messages = (messagesRes.data || []).filter(m => {
        const msgDate = new Date(m.created_at);
        return msgDate >= new Date(dateRange.start) && msgDate <= new Date(dateRange.end);
      });

      // Get agency info
      let agencyName = 'MSC Agency';
      try {
        const settingsRes = await api.get('/settings/agency');
        agencyName = settingsRes.data?.agency_name || agencyName;
      } catch (e) {}

      // Create PDF
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPos = 20;

      // Header
      doc.setFontSize(20);
      doc.setTextColor(26, 54, 93);
      doc.text(agencyName, pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;

      doc.setFontSize(14);
      doc.setTextColor(100, 116, 139);
      doc.text('Rapport de projet', pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;

      // Project info
      doc.setFontSize(16);
      doc.setTextColor(26, 54, 93);
      doc.text(project.name || 'Projet', 15, yPos);
      yPos += 8;

      doc.setFontSize(10);
      doc.setTextColor(100, 116, 139);
      doc.text(`Client: ${project.client_name || 'Non spécifié'}`, 15, yPos);
      yPos += 5;
      doc.text(`Période: ${new Date(dateRange.start).toLocaleDateString('fr-FR')} - ${new Date(dateRange.end).toLocaleDateString('fr-FR')}`, 15, yPos);
      yPos += 5;
      doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, 15, yPos);
      yPos += 15;

      // Project summary
      doc.setFontSize(12);
      doc.setTextColor(26, 54, 93);
      doc.text('Résumé du projet', 15, yPos);
      yPos += 8;

      // Helper for PDF number formatting
      const formatPdfNum = (num) => {
        if (typeof num !== 'number' || isNaN(num)) return '0';
        const rounded = Math.round(num * 100) / 100;
        const parts = rounded.toString().split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        return parts.length > 1 && parts[1] !== '0' ? parts.join(',') : parts[0];
      };

      const summaryData = [
        ['Statut', project.status === 'completed' ? 'Terminé' : project.status === 'active' ? 'En cours' : project.status],
        ['Budget', `${formatPdfNum(project.budget || 0)} €`],
        ['Progression', `${project.progress || 0}%`],
        ['Tâches totales', tasks.length.toString()],
        ['Tâches terminées', tasks.filter(t => t.status === 'completed').length.toString()],
      ];

      doc.autoTable({
        startY: yPos,
        head: [],
        body: summaryData,
        theme: 'plain',
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 50 },
          1: { cellWidth: 80 }
        },
        margin: { left: 15 }
      });

      yPos = doc.lastAutoTable.finalY + 15;

      // Tasks table
      if (tasks.length > 0) {
        doc.setFontSize(12);
        doc.setTextColor(26, 54, 93);
        doc.text('Tâches du projet', 15, yPos);
        yPos += 5;

        const taskRows = tasks.map(t => [
          t.name || '',
          t.assigned_to_name || 'Non assigné',
          `${t.actual_hours?.toFixed(1) || 0}h / ${t.estimated_hours || 0}h`,
          t.status === 'completed' ? 'Terminée' : t.status === 'in_progress' ? 'En cours' : 'En attente'
        ]);

        doc.autoTable({
          startY: yPos,
          head: [['Tâche', 'Assigné', 'Temps', 'Statut']],
          body: taskRows,
          theme: 'striped',
          headStyles: { fillColor: [26, 54, 93] },
          margin: { left: 15, right: 15 }
        });

        yPos = doc.lastAutoTable.finalY + 15;
      }

      // Activity summary
      if (messages.length > 0) {
        // Check if we need a new page
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }

        doc.setFontSize(12);
        doc.setTextColor(26, 54, 93);
        doc.text(`Activité sur la période (${messages.length} messages)`, 15, yPos);
        yPos += 8;

        doc.setFontSize(9);
        doc.setTextColor(100, 116, 139);
        doc.text(`Dernier message: ${messages.length > 0 ? new Date(messages[messages.length - 1].created_at).toLocaleDateString('fr-FR') : 'Aucun'}`, 15, yPos);
      }

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(`Page ${i} / ${pageCount}`, pageWidth / 2, 290, { align: 'center' });
        doc.text(`© ${new Date().getFullYear()} ${agencyName}`, pageWidth - 15, 290, { align: 'right' });
      }

      // Download
      const fileName = `Rapport_${project.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'projet'}_${dateRange.start}_${dateRange.end}.pdf`;
      doc.save(fileName);
      
      toast.success('Rapport PDF généré !');
      setShowProjectReportModal(false);

    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Erreur lors de la génération du PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Generate Client PDF Report
  const generateClientPDF = async () => {
    if (!selectedClientId) {
      toast.error('Veuillez sélectionner un client');
      return;
    }

    setGeneratingPdf(true);
    try {
      // Find selected client
      const client = clients.find(c => c.id === selectedClientId);
      const clientName = client?.company_name || client?.name || `${client?.first_name || ''} ${client?.last_name || ''}`.trim() || 'Client';
      
      // Fetch client projects and invoices
      const [clientProjectsRes, invoicesRes] = await Promise.all([
        api.get('/projects/', { params: { client_id: selectedClientId } }),
        api.get('/invoices/')
      ]);

      const clientProjects = clientProjectsRes.data || [];
      const allInvoices = invoicesRes.data || [];
      
      // Filter invoices for this client
      const clientInvoices = allInvoices.filter(inv => inv.client_id === selectedClientId);

      // Filter by date range
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      
      const filteredProjects = clientProjects.filter(p => {
        const createdAt = new Date(p.created_at);
        return createdAt >= startDate && createdAt <= endDate;
      });
      
      const filteredInvoices = clientInvoices.filter(inv => {
        const invDate = new Date(inv.created_at || inv.date);
        return invDate >= startDate && invDate <= endDate;
      });

      // Calculate totals
      const totalInvoiced = filteredInvoices.reduce((sum, inv) => sum + (inv.amount || 0), 0);
      const totalPaid = filteredInvoices.filter(i => i.status === 'paid').reduce((sum, inv) => sum + (inv.amount || 0), 0);

      // Helper function for PDF numbers
      const formatPdfNum = (num) => {
        if (typeof num !== 'number' || isNaN(num)) return '0';
        const rounded = Math.round(num * 100) / 100;
        const parts = rounded.toString().split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        return parts.length > 1 && parts[1] !== '0' ? parts.join(',') : parts[0];
      };

      // Create PDF
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPos = 20;

      // Header
      doc.setFillColor(26, 54, 93);
      doc.rect(0, 0, pageWidth, 40, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text(theme?.agency?.name || 'MSC Agency', pageWidth / 2, 18, { align: 'center' });
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'normal');
      doc.text('Rapport Client', pageWidth / 2, 30, { align: 'center' });
      
      yPos = 55;

      // Client info
      doc.setFontSize(16);
      doc.setTextColor(26, 54, 93);
      doc.setFont('helvetica', 'bold');
      doc.text(clientName, 14, yPos);
      yPos += 8;

      doc.setFontSize(10);
      doc.setTextColor(100, 116, 139);
      doc.setFont('helvetica', 'normal');
      doc.text(`Période: ${new Date(dateRange.start).toLocaleDateString('fr-FR')} - ${new Date(dateRange.end).toLocaleDateString('fr-FR')}`, 14, yPos);
      yPos += 15;

      // Summary section
      doc.setFontSize(14);
      doc.setTextColor(26, 54, 93);
      doc.setFont('helvetica', 'bold');
      doc.text('Résumé', 14, yPos);
      yPos += 10;

      const summaryData = [
        ['Projets sur la période', filteredProjects.length.toString()],
        ['Projets terminés', filteredProjects.filter(p => p.status === 'completed' || p.status === 'delivered').length.toString()],
        ['Total facturé', `${formatPdfNum(totalInvoiced)} €`],
        ['Total payé', `${formatPdfNum(totalPaid)} €`],
        ['En attente', `${formatPdfNum(totalInvoiced - totalPaid)} €`]
      ];

      doc.autoTable({
        startY: yPos,
        head: [],
        body: summaryData,
        theme: 'plain',
        styles: { fontSize: 10 },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 60 },
          1: { cellWidth: 80 }
        },
        margin: { left: 14 }
      });

      yPos = doc.lastAutoTable.finalY + 15;

      // Projects table
      if (filteredProjects.length > 0) {
        doc.setFontSize(14);
        doc.setTextColor(26, 54, 93);
        doc.setFont('helvetica', 'bold');
        doc.text('Projets', 14, yPos);
        yPos += 5;

        const projectTypeLabels = {
          website: 'Site Internet',
          logo: 'Logo',
          video: 'Vidéo',
          visual: 'Visuel',
          mobile_app: 'App mobile'
        };

        const statusLabels = {
          draft: 'Brouillon',
          in_progress: 'En cours',
          active: 'En cours',
          completed: 'Terminé',
          delivered: 'Livré',
          cancelled: 'Annulé'
        };

        const projectData = filteredProjects.map(p => [
          p.name || 'Sans nom',
          projectTypeLabels[p.project_type] || p.project_type || '-',
          statusLabels[p.status] || p.status || '-',
          `${formatPdfNum(p.budget || 0)} €`
        ]);

        doc.autoTable({
          startY: yPos,
          head: [['Projet', 'Type', 'Statut', 'Budget']],
          body: projectData,
          theme: 'striped',
          headStyles: { fillColor: [26, 54, 93] },
          styles: { fontSize: 9 },
          margin: { left: 14, right: 14 }
        });

        yPos = doc.lastAutoTable.finalY + 15;
      }

      // Invoices table
      if (filteredInvoices.length > 0) {
        if (yPos > 200) {
          doc.addPage();
          yPos = 20;
        }

        doc.setFontSize(14);
        doc.setTextColor(26, 54, 93);
        doc.setFont('helvetica', 'bold');
        doc.text('Factures', 14, yPos);
        yPos += 5;

        const invoiceStatusLabels = {
          pending: 'En attente',
          sent: 'Envoyée',
          paid: 'Payée',
          rejected: 'Rejetée'
        };

        const invoiceData = filteredInvoices.map(inv => [
          inv.invoice_number || inv.id?.slice(0, 8) || '-',
          new Date(inv.created_at || inv.date).toLocaleDateString('fr-FR'),
          `${formatPdfNum(inv.amount || 0)} €`,
          invoiceStatusLabels[inv.status] || inv.status || '-'
        ]);

        doc.autoTable({
          startY: yPos,
          head: [['Numéro', 'Date', 'Montant', 'Statut']],
          body: invoiceData,
          theme: 'striped',
          headStyles: { fillColor: [26, 54, 93] },
          styles: { fontSize: 9 },
          margin: { left: 14, right: 14 }
        });
      }

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(`Page ${i} / ${pageCount}`, pageWidth / 2, 290, { align: 'center' });
        doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')}`, pageWidth - 14, 290, { align: 'right' });
      }

      // Download
      const fileName = `Rapport_Client_${clientName.replace(/[^a-zA-Z0-9]/g, '_')}_${dateRange.start}_${dateRange.end}.pdf`;
      doc.save(fileName);
      
      toast.success('Rapport client généré !');
      setShowClientReportModal(false);
    } catch (error) {
      console.error('Error generating client report:', error);
      toast.error('Erreur lors de la génération du rapport');
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Generate Creative PDF Report
  const generateCreativePDF = async () => {
    if (!selectedCreativeId) {
      toast.error('Veuillez sélectionner un créatif');
      return;
    }

    setGeneratingPdf(true);
    try {
      // Find selected creative
      const creative = creatives.find(c => c.id === selectedCreativeId);
      const creativeName = `${creative?.first_name || ''} ${creative?.last_name || ''}`.trim() || 'Créatif';
      
      // Fetch time entries and tasks for this creative
      const [timeEntriesRes, tasksRes] = await Promise.all([
        api.get('/time/entries/', { params: { user_id: selectedCreativeId } }).catch(() => ({ data: [] })),
        api.get('/tasks/').catch(() => ({ data: [] }))
      ]);

      const allTimeEntries = timeEntriesRes.data || [];
      const allTasks = tasksRes.data || [];
      
      // Filter by date range
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      
      const filteredTimeEntries = allTimeEntries.filter(entry => {
        const entryDate = new Date(entry.start_time || entry.created_at);
        return entryDate >= startDate && entryDate <= endDate;
      });

      // Filter tasks assigned to this creative
      const creativeTasks = allTasks.filter(t => 
        t.assigned_to === selectedCreativeId || t.assigned_to_id === selectedCreativeId
      );

      const filteredTasks = creativeTasks.filter(t => {
        const taskDate = new Date(t.created_at);
        return taskDate >= startDate && taskDate <= endDate;
      });

      // Calculate totals
      const totalHours = filteredTimeEntries.reduce((sum, entry) => {
        const duration = entry.duration_seconds || entry.duration || 0;
        return sum + (duration / 3600);
      }, 0);

      const completedTasks = filteredTasks.filter(t => t.status === 'completed' || t.status === 'done' || t.status === 'validated');
      const totalEarnings = filteredTimeEntries.reduce((sum, entry) => sum + (entry.earnings || 0), 0);

      // Helper function for PDF numbers
      const formatPdfNum = (num) => {
        if (typeof num !== 'number' || isNaN(num)) return '0';
        const rounded = Math.round(num * 100) / 100;
        const parts = rounded.toString().split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        return parts.length > 1 && parts[1] !== '0' ? parts.join(',') : parts[0];
      };

      // Create PDF
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPos = 20;

      // Header
      doc.setFillColor(107, 70, 193); // Purple for creatives
      doc.rect(0, 0, pageWidth, 40, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text(theme?.agency?.name || 'MSC Agency', pageWidth / 2, 18, { align: 'center' });
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'normal');
      doc.text('Rapport Créatif', pageWidth / 2, 30, { align: 'center' });
      
      yPos = 55;

      // Creative info
      doc.setFontSize(16);
      doc.setTextColor(26, 54, 93);
      doc.setFont('helvetica', 'bold');
      doc.text(creativeName, 14, yPos);
      yPos += 8;

      if (creative?.email) {
        doc.setFontSize(10);
        doc.setTextColor(100, 116, 139);
        doc.setFont('helvetica', 'normal');
        doc.text(creative.email, 14, yPos);
        yPos += 5;
      }

      doc.setFontSize(10);
      doc.setTextColor(100, 116, 139);
      doc.text(`Période: ${new Date(dateRange.start).toLocaleDateString('fr-FR')} - ${new Date(dateRange.end).toLocaleDateString('fr-FR')}`, 14, yPos);
      yPos += 15;

      // Summary section
      doc.setFontSize(14);
      doc.setTextColor(26, 54, 93);
      doc.setFont('helvetica', 'bold');
      doc.text('Performance', 14, yPos);
      yPos += 10;

      const summaryData = [
        ['Heures travaillées', `${totalHours.toFixed(1)}h`],
        ['Tâches assignées', filteredTasks.length.toString()],
        ['Tâches terminées', completedTasks.length.toString()],
        ['Taux de complétion', filteredTasks.length > 0 ? `${Math.round((completedTasks.length / filteredTasks.length) * 100)}%` : 'N/A'],
        ['Revenus générés', `${formatPdfNum(totalEarnings)} €`]
      ];

      doc.autoTable({
        startY: yPos,
        head: [],
        body: summaryData,
        theme: 'plain',
        styles: { fontSize: 10 },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 60 },
          1: { cellWidth: 80 }
        },
        margin: { left: 14 }
      });

      yPos = doc.lastAutoTable.finalY + 15;

      // Tasks table
      if (filteredTasks.length > 0) {
        doc.setFontSize(14);
        doc.setTextColor(26, 54, 93);
        doc.setFont('helvetica', 'bold');
        doc.text('Tâches', 14, yPos);
        yPos += 5;

        const statusLabels = {
          pending: 'En attente',
          todo: 'À faire',
          in_progress: 'En cours',
          completed: 'Terminée',
          done: 'Terminée',
          validated: 'Validée',
          cancelled: 'Annulée'
        };

        const taskData = filteredTasks.map(t => [
          t.name || t.title || 'Sans nom',
          t.project_name || '-',
          `${(t.actual_hours || 0).toFixed(1)}h`,
          statusLabels[t.status] || t.status || '-'
        ]);

        doc.autoTable({
          startY: yPos,
          head: [['Tâche', 'Projet', 'Temps', 'Statut']],
          body: taskData,
          theme: 'striped',
          headStyles: { fillColor: [107, 70, 193] },
          styles: { fontSize: 9 },
          margin: { left: 14, right: 14 }
        });

        yPos = doc.lastAutoTable.finalY + 15;
      }

      // Time entries summary by project
      if (filteredTimeEntries.length > 0) {
        if (yPos > 200) {
          doc.addPage();
          yPos = 20;
        }

        doc.setFontSize(14);
        doc.setTextColor(26, 54, 93);
        doc.setFont('helvetica', 'bold');
        doc.text('Détail des heures', 14, yPos);
        yPos += 5;

        // Group by project
        const hoursByProject = {};
        filteredTimeEntries.forEach(entry => {
          const projectName = entry.project_name || 'Autre';
          const hours = (entry.duration_seconds || entry.duration || 0) / 3600;
          hoursByProject[projectName] = (hoursByProject[projectName] || 0) + hours;
        });

        const hoursData = Object.entries(hoursByProject).map(([project, hours]) => [
          project,
          `${hours.toFixed(1)}h`
        ]);

        doc.autoTable({
          startY: yPos,
          head: [['Projet', 'Heures']],
          body: hoursData,
          theme: 'striped',
          headStyles: { fillColor: [107, 70, 193] },
          styles: { fontSize: 9 },
          margin: { left: 14, right: 14 }
        });
      }

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(`Page ${i} / ${pageCount}`, pageWidth / 2, 290, { align: 'center' });
        doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')}`, pageWidth - 14, 290, { align: 'right' });
      }

      // Download
      const fileName = `Rapport_Creatif_${creativeName.replace(/[^a-zA-Z0-9]/g, '_')}_${dateRange.start}_${dateRange.end}.pdf`;
      doc.save(fileName);
      
      toast.success('Rapport créatif généré !');
      setShowCreativeReportModal(false);
    } catch (error) {
      console.error('Error generating creative report:', error);
      toast.error('Erreur lors de la génération du rapport');
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Generate Financial PDF Report
  const generateFinancialPDF = async () => {
    setGeneratingPdf(true);
    try {
      // Fetch all necessary data
      const [projectsRes, invoicesRes, expensesRes] = await Promise.all([
        api.get('/projects/'),
        api.get('/invoices/'),
        api.get('/expenses/')
      ]);

      const allProjects = projectsRes.data || [];
      const allInvoices = invoicesRes.data || [];
      const allExpenses = expensesRes.data || [];

      // Filter by date range
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      
      const filteredProjects = allProjects.filter(p => {
        const createdAt = new Date(p.created_at);
        return createdAt >= startDate && createdAt <= endDate;
      });
      
      const filteredInvoices = allInvoices.filter(inv => {
        const invDate = new Date(inv.created_at || inv.date);
        return invDate >= startDate && invDate <= endDate;
      });

      const filteredExpenses = allExpenses.filter(exp => {
        const expDate = new Date(exp.created_at || exp.date);
        return expDate >= startDate && expDate <= endDate;
      });

      // Calculate totals
      const totalRevenue = filteredInvoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.amount || 0), 0);
      const totalExpenses = filteredExpenses.filter(e => e.status === 'approved').reduce((sum, e) => sum + (e.amount || 0), 0);
      const margin = totalRevenue - totalExpenses;
      const marginPercent = totalRevenue > 0 ? ((margin / totalRevenue) * 100).toFixed(1) : 0;

      // Revenue by service type
      const revenueByType = {};
      filteredProjects.forEach(p => {
        const type = p.project_type || 'Autre';
        revenueByType[type] = (revenueByType[type] || 0) + (p.budget || 0);
      });

      // Projects delivered
      const deliveredProjects = filteredProjects.filter(p => p.status === 'completed' || p.status === 'delivered');

      // Helper function for PDF numbers
      const formatPdfNum = (num) => {
        if (typeof num !== 'number' || isNaN(num)) return '0';
        const rounded = Math.round(num * 100) / 100;
        const parts = rounded.toString().split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        return parts.length > 1 && parts[1] !== '0' ? parts.join(',') : parts[0];
      };

      // Create PDF
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPos = 20;

      // Header
      doc.setFillColor(26, 54, 93);
      doc.rect(0, 0, pageWidth, 40, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text(theme?.agency?.name || 'MSC Agency', pageWidth / 2, 18, { align: 'center' });
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'normal');
      doc.text('Rapport Financier', pageWidth / 2, 30, { align: 'center' });
      
      yPos = 55;

      // Period
      doc.setTextColor(100, 116, 139);
      doc.setFontSize(10);
      doc.text(`Période: ${new Date(dateRange.start).toLocaleDateString('fr-FR')} - ${new Date(dateRange.end).toLocaleDateString('fr-FR')}`, 14, yPos);
      yPos += 15;

      // Summary section
      doc.setFontSize(14);
      doc.setTextColor(26, 54, 93);
      doc.setFont('helvetica', 'bold');
      doc.text('Résumé Financier', 14, yPos);
      yPos += 10;

      const summaryData = [
        ['Chiffre d\'affaires', `${formatPdfNum(totalRevenue)} €`],
        ['Dépenses', `${formatPdfNum(totalExpenses)} €`],
        ['Marge nette', `${formatPdfNum(margin)} € (${marginPercent}%)`],
        ['Projets livrés', deliveredProjects.length.toString()],
        ['Factures payées', filteredInvoices.filter(i => i.status === 'paid').length.toString()]
      ];

      doc.autoTable({
        startY: yPos,
        head: [],
        body: summaryData,
        theme: 'plain',
        styles: { fontSize: 10 },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 60 },
          1: { cellWidth: 80 }
        },
        margin: { left: 14 }
      });

      yPos = doc.lastAutoTable.finalY + 15;

      // Revenue by service type
      if (Object.keys(revenueByType).length > 0) {
        doc.setFontSize(14);
        doc.setTextColor(26, 54, 93);
        doc.setFont('helvetica', 'bold');
        doc.text('Répartition du CA par type de prestation', 14, yPos);
        yPos += 5;

        const typeLabels = {
          website: 'Site Internet',
          web: 'Site Internet',
          branding: 'Branding',
          video: 'Vidéo',
          social_media: 'Social Media',
          print: 'Print',
          other: 'Autre'
        };

        const typeData = Object.entries(revenueByType).map(([type, amount]) => [
          typeLabels[type] || type,
          `${formatPdfNum(amount)} €`
        ]);

        doc.autoTable({
          startY: yPos,
          head: [['Type de prestation', 'Montant']],
          body: typeData,
          theme: 'striped',
          headStyles: { fillColor: [26, 54, 93] },
          styles: { fontSize: 9 },
          margin: { left: 14, right: 14 }
        });

        yPos = doc.lastAutoTable.finalY + 15;
      }

      // Delivered projects
      if (deliveredProjects.length > 0) {
        doc.setFontSize(14);
        doc.setTextColor(26, 54, 93);
        doc.setFont('helvetica', 'bold');
        doc.text('Projets livrés', 14, yPos);
        yPos += 5;

        const projectData = deliveredProjects.map(p => [
          p.name || 'Sans nom',
          p.client_name || 'N/A',
          `${formatPdfNum(p.budget || 0)} €`,
          p.deadline ? new Date(p.deadline).toLocaleDateString('fr-FR') : '-'
        ]);

        doc.autoTable({
          startY: yPos,
          head: [['Projet', 'Client', 'Budget', 'Livraison']],
          body: projectData,
          theme: 'striped',
          headStyles: { fillColor: [26, 54, 93] },
          styles: { fontSize: 9 },
          margin: { left: 14, right: 14 }
        });
      }

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')} - Page ${i}/${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
      }

      // Download
      doc.save(`rapport_financier_${dateRange.start}_${dateRange.end}.pdf`);
      toast.success('Rapport financier généré !');
      setShowFinancialReportModal(false);
    } catch (error) {
      console.error('Error generating financial report:', error);
      toast.error('Erreur lors de la génération du rapport');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const StatCard = ({ title, value, subValue, icon: Icon, trend, trendValue, color = 'blue' }) => (
    <Card className="overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-1">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            {subValue && <p className="text-sm text-muted-foreground mt-1">{subValue}</p>}
          </div>
          <div className={cn(
            "p-3 rounded-xl",
            color === 'blue' && "bg-blue-100 text-blue-600",
            color === 'green' && "bg-green-100 text-green-600",
            color === 'orange' && "bg-orange-100 text-orange-600",
            color === 'purple' && "bg-purple-100 text-purple-600",
            color === 'red' && "bg-red-100 text-red-600"
          )}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
        {trend !== undefined && (
          <div className={cn(
            "flex items-center gap-1 mt-3 text-sm font-medium",
            trend >= 0 ? "text-green-600" : "text-red-600"
          )}>
            {trend >= 0 ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
            <span>{Math.abs(trend)}% {trendValue}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      
      <div className={cn(
        "flex-1 flex flex-col transition-all duration-300",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        <Header setMobileOpen={setMobileOpen} />
        
        <main className="flex-1 p-4 md:p-6 pt-32 overflow-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold">Rapports & Analyses</h1>
              <p className="text-muted-foreground">Vue d'ensemble de la performance de l'agence</p>
            </div>
            <div className="flex items-center gap-3">
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-[160px]">
                  <Calendar className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">Cette semaine</SelectItem>
                  <SelectItem value="month">Ce mois</SelectItem>
                  <SelectItem value="quarter">Ce trimestre</SelectItem>
                  <SelectItem value="year">Cette année</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchReportData}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Actualiser
              </Button>
              <Button variant="outline" onClick={() => setShowProjectReportModal(true)}>
                <FileText className="h-4 w-4 mr-2" />
                Rapport projet
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard
                  title="Chiffre d'affaires"
                  value={formatCurrency(stats.revenue.total)}
                  subValue="Factures payées"
                  icon={Euro}
                  trend={stats.revenue.growth}
                  trendValue="vs mois dernier"
                  color="green"
                />
                <StatCard
                  title="Dépenses"
                  value={formatCurrency(stats.expenses.total)}
                  subValue="Total des charges"
                  icon={TrendingDown}
                  trend={stats.expenses.growth}
                  trendValue="vs mois dernier"
                  color="red"
                />
                <StatCard
                  title="Marge nette"
                  value={formatCurrency(stats.profit.total)}
                  subValue={`${stats.profit.margin}% de marge`}
                  icon={Target}
                  color="blue"
                />
                <StatCard
                  title="Projets actifs"
                  value={stats.projects.active}
                  subValue={`${stats.projects.completed} terminés ce mois`}
                  icon={FolderKanban}
                  color="purple"
                />
              </div>

              {/* Secondary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-blue-100 text-blue-600 rounded-xl">
                        <Users className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Clients</p>
                        <p className="text-xl font-bold">{stats.clients.total}</p>
                        <p className="text-xs text-green-600">+{stats.clients.new} nouveaux ce mois</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-orange-100 text-orange-600 rounded-xl">
                        <FileText className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Factures en attente</p>
                        <p className="text-xl font-bold">{stats.invoices.pending}</p>
                        {stats.invoices.overdue > 0 && (
                          <p className="text-xs text-red-600">{stats.invoices.overdue} en retard</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-green-100 text-green-600 rounded-xl">
                        <Briefcase className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Taux de conversion</p>
                        <p className="text-xl font-bold">68%</p>
                        <p className="text-xs text-green-600">+5% vs mois dernier</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Charts Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Revenue by Client */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-blue-600" />
                      Top Clients par CA
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {revenueByClient.length > 0 ? (
                        revenueByClient.map((client, index) => (
                          <div key={client.name} className="flex items-center gap-4">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-sm">
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium">{client.name}</span>
                                <span className="text-sm font-semibold">{formatCurrency(client.amount)}</span>
                              </div>
                              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
                                  style={{ width: `${Math.min((client.amount / (revenueByClient[0]?.amount || 1)) * 100, 100)}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-center text-muted-foreground py-8">Aucune donnée disponible</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Revenue by Service */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <PieChart className="h-5 w-5 text-purple-600" />
                      Répartition par service
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {revenueByService.map((service, index) => {
                        const colors = ['bg-blue-500', 'bg-purple-500', 'bg-green-500', 'bg-orange-500', 'bg-gray-400'];
                        return (
                          <div key={service.name} className="flex items-center gap-4">
                            <div className={cn("w-3 h-3 rounded-full", colors[index])} />
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium">{service.name}</span>
                                <div className="text-right">
                                  <span className="text-sm font-semibold">{formatCurrency(service.amount)}</span>
                                  <span className="text-xs text-muted-foreground ml-2">({service.percentage}%)</span>
                                </div>
                              </div>
                              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div 
                                  className={cn("h-full rounded-full", colors[index])}
                                  style={{ width: `${service.percentage}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Monthly Trend */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    Évolution mensuelle
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-12 gap-4">
                    {monthlyTrend.map((data, index) => (
                      <div key={data.month} className="text-center">
                        <p className="text-xs text-muted-foreground mb-2">{data.month}</p>
                        <div className="relative h-24 bg-gray-100 rounded-lg overflow-hidden">
                          <div 
                            className="absolute bottom-0 left-0 right-0 bg-green-500/20 border-t-2 border-green-500"
                            style={{ height: `${Math.min((data.revenue / (Math.max(...monthlyTrend.map(m => m.revenue)) || 1)) * 100, 100)}%` }}
                          />
                        </div>
                        <p className="text-xs font-semibold mt-2">{formatCurrency(data.revenue)}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full" />
                      <span className="text-sm text-muted-foreground">Revenus</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Actions rapides</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => setShowFinancialReportModal(true)}>
                      <FileText className="h-5 w-5" />
                      <span className="text-sm">Rapport financier</span>
                    </Button>
                    <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => setShowClientReportModal(true)}>
                      <Users className="h-5 w-5" />
                      <span className="text-sm">Rapport clients</span>
                    </Button>
                    <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => setShowProjectReportModal(true)}>
                      <FolderKanban className="h-5 w-5" />
                      <span className="text-sm">Rapport projets</span>
                    </Button>
                    <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => setShowCreativeReportModal(true)}>
                      <User className="h-5 w-5" />
                      <span className="text-sm">Rapport créatifs</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </main>
      </div>

      {/* Project Report Modal */}
      <Dialog open={showProjectReportModal} onOpenChange={setShowProjectReportModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Générer un rapport projet</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Projet</Label>
              <Select value={selectedProjectId || '_none'} onValueChange={(v) => setSelectedProjectId(v === '_none' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un projet..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none" disabled>Sélectionner un projet...</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date de début</Label>
                <Input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                />
              </div>
              <div>
                <Label>Date de fin</Label>
                <Input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                />
              </div>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                Le rapport inclura : résumé du projet, liste des tâches, temps passé et activité sur la période.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProjectReportModal(false)}>
              Annuler
            </Button>
            <Button onClick={generateProjectPDF} disabled={generatingPdf || !selectedProjectId}>
              {generatingPdf ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Générer PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Report Modal */}
      <Dialog open={showClientReportModal} onOpenChange={setShowClientReportModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              Rapport client
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Client</Label>
              <Select value={selectedClientId || '_none'} onValueChange={(v) => setSelectedClientId(v === '_none' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un client..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none" disabled>Sélectionner un client...</SelectItem>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name || c.company_name || c.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date de début</Label>
                <Input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                />
              </div>
              <div>
                <Label>Date de fin</Label>
                <Input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                />
              </div>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                Le rapport inclura : projets du client, factures, statistiques et historique d'activité.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClientReportModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={generateClientPDF}
              disabled={generatingPdf || !selectedClientId}
            >
              {generatingPdf ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Générer PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Creative Report Modal */}
      <Dialog open={showCreativeReportModal} onOpenChange={setShowCreativeReportModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-purple-600" />
              Rapport créatif
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Créatif</Label>
              <Select value={selectedCreativeId || '_none'} onValueChange={(v) => setSelectedCreativeId(v === '_none' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un créatif..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none" disabled>Sélectionner un créatif...</SelectItem>
                  {creatives.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date de début</Label>
                <Input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                />
              </div>
              <div>
                <Label>Date de fin</Label>
                <Input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                />
              </div>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <p className="text-sm text-purple-700">
                Le rapport inclura : projets assignés, heures travaillées, tâches et performance.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreativeReportModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={generateCreativePDF}
              disabled={generatingPdf || !selectedCreativeId}
            >
              {generatingPdf ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Générer PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Financial Report Modal */}
      <Dialog open={showFinancialReportModal} onOpenChange={setShowFinancialReportModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Euro className="h-5 w-5 text-green-600" />
              Rapport Financier
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date de début</Label>
                <Input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                />
              </div>
              <div>
                <Label>Date de fin</Label>
                <Input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                />
              </div>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <p className="text-sm text-green-700">
                Le rapport inclura : chiffre d'affaires, dépenses, marge nette, projets livrés et répartition du CA par type de prestation.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFinancialReportModal(false)}>
              Annuler
            </Button>
            <Button onClick={generateFinancialPDF} disabled={generatingPdf}>
              {generatingPdf ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Générer PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportsPage;
